import type { ReactElement } from 'react';
import { useContext, useLayoutEffect, useRef, useState } from 'react';
import { sliderContext } from '../SliderContext';

interface CountProps {
  seconds: number;
  active: boolean;
  autoplayProgressBar: boolean;
  autoplayPauseOnHover: boolean;
  last: boolean;
  children: ({ percent }: { percent: number }) => ReactElement;
  index?: number;
}

export const Count = ({
  seconds,
  children,
  active,
  autoplayProgressBar,
  autoplayPauseOnHover,
  last,
  index
}: CountProps) => {
  const [percent, setPercent] = useState(0);
  const { isHovered } = useContext(sliderContext);
  const startTimeRef = useRef<number | null>(null);
  const requestRef = useRef<number>();
  const lastIndexRef = useRef(index);

  useLayoutEffect(() => {
    if (lastIndexRef.current !== index) {
      setPercent(0);
      startTimeRef.current = null;
      lastIndexRef.current = index;
    }

    const shouldReset =
      !active ||
      !autoplayProgressBar ||
      last ||
      (isHovered && autoplayPauseOnHover);

    if (shouldReset) {
      setPercent(0);
      startTimeRef.current = null;

      return;
    }

    const animate = (timestamp: number) => {
      if (startTimeRef.current === null) {
        startTimeRef.current = timestamp;
      }

      const elapsed = timestamp - startTimeRef.current;
      const newPercent = Math.min((elapsed / (seconds * 1000)) * 100, 100);
      setPercent(newPercent);

      if (newPercent < 100) {
        requestRef.current = requestAnimationFrame(animate);
      }
    };

    requestRef.current = requestAnimationFrame(animate);

    return () => {
      if (requestRef.current) {
        cancelAnimationFrame(requestRef.current);
      }
    };
  }, [
    active,
    seconds,
    isHovered,
    autoplayProgressBar,
    autoplayPauseOnHover,
    last,
    index
  ]);

  return children({ percent });
};
